<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12 component-title-row">
          <h3>
            <i class="fa fa-fw fa-sliders"></i> {{ _('Partner API') }}
          </h3>
          <div class="component-title-buttons">
            <button class="btn btn-default" type="button" v-on:click="reload" :disabled="loading"><i :class="'fa fa-fw fa-' + (loading ? 'cog fa-spin' : 'refresh')"></i> {{ _('Refresh') }}</button>
          </div>
        </div>
      </div>
      <h4>{{ _('Endpoints') }}</h4>
      <p class="help-block">{{ _('A disabled endpoint answers 503 with the message below. The global limit caps the requests per minute from all partners together; the cost is how much one request uses of a partner\'s quota.') }}</p>
      <div class="partner-table-wrap" v-if="policies.length">
        <table class="table table-striped table-condensed policy-table">
          <thead>
            <tr>
              <th>{{ _('Endpoint') }}</th>
              <th class="text-center">{{ _('Enabled') }}</th>
              <th>{{ _('Global limit / min') }}</th>
              <th>{{ _('Cost') }}</th>
              <th>{{ _('Message when disabled') }}</th>
              <th class="text-right">{{ _('Requests, 24 h') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="policy in policies" :key="policy.endpoint_id" :class="{ 'danger': !policy.enabled, 'warning': isDirty(policy) && policy.enabled }">
              <td>
                <code>{{ policy.method }} {{ policy.path }}</code>
                <div class="small text-muted">{{ policy.endpoint_id }}</div>
              </td>
              <td class="text-center">
                <input type="checkbox" v-model="policy.enabled" :aria-label="_('Enabled')" />
              </td>
              <td>
                <input class="form-control input-sm policy-number" type="number" min="0" step="1" v-model="policy.global_rpm" :placeholder="_('No limit')" />
              </td>
              <td>
                <input class="form-control input-sm policy-number" type="number" min="0" step="1" v-model="policy.cost" />
              </td>
              <td>
                <input class="form-control input-sm" type="text" v-model="policy.disabled_message" :placeholder="_('The endpoint is temporarily unavailable.')" />
              </td>
              <td class="text-right">{{ formatCount(policy.requests_24h) }}</td>
              <td class="text-right">
                <button type="button" class="btn btn-success btn-sm" v-on:click="savePolicy(policy)" :disabled="!isDirty(policy) || savingId === policy.endpoint_id">
                  <i :class="'fa fa-fw fa-' + (savingId === policy.endpoint_id ? 'cog fa-spin' : 'save')"></i> {{ _('Save') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-muted" v-else-if="!loading">{{ _('No endpoints.') }}</p>
      <h4>{{ _('Traffic per partner, last 24 h') }}</h4>
      <div class="partner-table-wrap" v-if="trafficPartners.length">
        <table class="table table-striped table-condensed table-hover">
          <thead>
            <tr>
              <th>{{ _('Partner') }}</th>
              <th>{{ _('Status') }}</th>
              <th class="text-right">{{ _('Requests, 24 h') }}</th>
              <th class="text-right">{{ _('Error rate') }}</th>
              <th>{{ _('Last used') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="partner-row" v-for="partner in trafficPartners" :key="partner.id" v-on:click="openPartner(partner)">
              <td>{{ partner.name }}</td>
              <td><span :class="statusClass(partner.status)">{{ statusLabel(partner.status) }}</span></td>
              <td class="text-right">{{ formatCount(partner.requests_24h) }}</td>
              <td class="text-right">{{ formatRate(partner.error_rate_24h) }}</td>
              <td>{{ formatApiTime(partner.last_used_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="text-muted" v-else-if="!loading">{{ _('No partner traffic in the last 24 hours.') }}</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import partnerapi from '~/lib/partnerapi'

export default {
  mixins: [components, partnerapi],
  data () {
    return {
      policies: [],
      originals: {},
      partners: [],
      savingId: null
    }
  },
  computed: {
    trafficPartners () {
      return this.partners
        .filter(p => Number(p.requests_24h) > 0)
        .sort((a, b) => Number(b.requests_24h) - Number(a.requests_24h))
    }
  },
  methods: {
    nullableInt (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const number = parseInt(value, 10)
      return Number.isNaN(number) ? null : number
    },
    policyPayload (policy) {
      return {
        endpoint_id: policy.endpoint_id,
        enabled: !!policy.enabled,
        global_rpm: this.nullableInt(policy.global_rpm),
        cost: this.nullableInt(policy.cost) ?? 1,
        disabled_message: policy.disabled_message || ''
      }
    },
    isDirty (policy) {
      return JSON.stringify(this.policyPayload(policy)) !== this.originals[policy.endpoint_id]
    },
    applyPolicy (policy) {
      policy.enabled = policy.enabled === true || parseInt(policy.enabled, 10) === 1
      policy.disabled_message = policy.disabled_message || ''
      this.originals[policy.endpoint_id] = JSON.stringify(this.policyPayload(policy))
      return policy
    },
    async loadPolicies () {
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/admin/partnerapipolicies.json?apikey=' + this.apiKey)
        this.originals = {}
        this.policies = this.listData(data).map(policy => this.applyPolicy({ ...policy }))
      } catch (e) {
        this.showError(e, this._('Loading the endpoints failed.'), true)
      }
    },
    async loadPartners () {
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/admin/partners.json?apikey=' + this.apiKey)
        this.partners = this.listData(data)
      } catch (e) {
        this.showError(e, this._('Loading the partners failed.'), true)
      }
    },
    async reload () {
      this.loading = true
      try {
        await Promise.all([this.loadPolicies(), this.loadPartners()])
      } finally {
        this.loading = false
      }
    },
    async savePolicy (policy) {
      this.savingId = policy.endpoint_id
      const payload = this.policyPayload(policy)
      try {
        const { data } = await axios.patch(this.baseDomain + this.shopId + '/admin/partnerapipolicies.json?apikey=' + this.apiKey, payload)
        if (data && data.endpoint_id === policy.endpoint_id) {
          Object.assign(policy, data)
        }
        this.applyPolicy(policy)
        this.showNotification(this._('Changes saved successfully!'))
      } catch (e) {
        this.showError(e, null, true)
      } finally {
        this.savingId = null
      }
    },
    openPartner (partner) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'AdminPartnerDetails',
        title: this._('Partner') + ': ' + partner.name,
        width: 6,
        data: {
          partnerId: partner.id
        }
      })
    }
  },
  mounted () {
    this.reload()
  }
}
</script>

<style scoped>
.partner-table-wrap {
  margin-top: 10px;
  overflow-x: auto;
}
.policy-table td {
  vertical-align: middle;
}
.policy-number {
  width: 90px;
}
.partner-row {
  cursor: pointer;
}
</style>
